import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, CheckCircle2, ChevronLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function LeaveReview() {
  const [form, setForm] = useState({ author: "", rating: 5, text: "", order_number: "", email: "", company: "" });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (!form.author.trim() || form.text.trim().length < 3) {
      setError("Please add your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews/submit", { ...form, rating: Number(form.rating) });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="leave-review-page" className="bg-[#fff8ec] min-h-[70vh]">
      <div className="jm-container py-12 max-w-xl">
        <Link to="/reviews" className="inline-flex items-center gap-1 text-navy/70 hover:text-brick text-sm font-bold">
          <ChevronLeft className="w-4 h-4" /> Back to Reviews
        </Link>

        {done ? (
          <div data-testid="review-success" className="mt-8 bg-white rounded-xl border border-navy/15 p-10 text-center">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
            <h1 className="mt-4 font-display text-3xl text-navy">Thank you!</h1>
            <p className="mt-3 text-ink/75 leading-relaxed">
              Your review was submitted and will appear publicly once it's approved. We appreciate you
              taking the time to share your experience.
            </p>
            <Link to="/reviews" className="mt-6 inline-block bg-brick text-white font-bold uppercase rounded-md px-6 py-3 hover:bg-navy transition-colors">
              View Reviews
            </Link>
          </div>
        ) : (
          <form data-testid="public-review-form" onSubmit={submit} className="mt-6 bg-white rounded-xl border border-navy/15 p-6 sm:p-8">
            <h1 className="font-display text-4xl text-navy">Leave a Review</h1>
            <p className="mt-2 text-ink/65">Share your experience with JohnnyMart. Reviews are moderated before they appear.</p>

            {/* Honeypot — hidden from real users */}
            <input type="text" name="company" tabIndex={-1} autoComplete="off" value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="hidden" aria-hidden="true" />

            <label className="block text-sm font-bold text-navy uppercase mt-6">Your name *</label>
            <input data-testid="review-author-input" required value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              className="mt-1 w-full rounded-md border border-navy/20 px-3 py-3 text-base outline-none focus:border-brick" />

            <label className="block text-sm font-bold text-navy uppercase mt-5">Rating *</label>
            <div data-testid="review-rating" className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`star-${n}`}
                  onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  onClick={() => setForm({ ...form, rating: n })} className="p-1">
                  <Star className={`w-9 h-9 ${n <= (hover || form.rating) ? "fill-gold text-gold" : "fill-none text-navy/25"}`} />
                </button>
              ))}
            </div>

            <label className="block text-sm font-bold text-navy uppercase mt-5">Your review *</label>
            <textarea data-testid="review-text-input" required rows={5} value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              className="mt-1 w-full rounded-md border border-navy/20 px-3 py-3 text-base outline-none focus:border-brick resize-none" />

            <label className="block text-sm font-bold text-navy uppercase mt-5">Order number <span className="text-ink/40 normal-case font-normal">(optional)</span></label>
            <input data-testid="review-order-input" value={form.order_number}
              onChange={(e) => setForm({ ...form, order_number: e.target.value })} placeholder="e.g. JM-1013"
              className="mt-1 w-full rounded-md border border-navy/20 px-3 py-3 text-base outline-none focus:border-brick" />

            <label className="block text-sm font-bold text-navy uppercase mt-5">Email <span className="text-ink/40 normal-case font-normal">(optional, never shown publicly)</span></label>
            <input data-testid="review-email-input" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full rounded-md border border-navy/20 px-3 py-3 text-base outline-none focus:border-brick" />

            {error && <p data-testid="review-error" className="mt-4 text-brick font-semibold text-sm">{error}</p>}

            <button data-testid="submit-review-btn" disabled={submitting}
              className="mt-6 w-full bg-brick text-white font-bold uppercase rounded-md py-4 text-base hover:bg-navy transition-colors disabled:opacity-60">
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
